import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { UnlockAttendancePermissionCard } from "@/components/UnlockAttendancePermissionCard";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ShieldCheck, KeyRound, LogOut, RefreshCw, Loader2, Info, Mail, RotateCcw,
} from "lucide-react";

/**
 * SA-only admin for per-user password login control on PB-MGT users.
 * Center Managers are OTP-only by policy and cannot appear here.
 * Endpoints:
 *  - /auth/security/admin/list-mgt-users
 *  - /auth/security/admin/toggle-password-login
 *  - /auth/security/admin/force-password-reset
 *  - /auth/security/admin/revoke-user-sessions
 */
export default function LoginSecurityAdmin() {
  const { session } = useAuth();
  const [rows, setRows] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);   // mobile currently being mutated
  // iter286 — SMTP diagnostic.  Must be above any early return so hook
  // ordering stays constant across renders.
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.post("/auth/security/admin/list-mgt-users",
        { token: session.token });
      setRows(r.data.users || []);
      setPolicy(r.data.cooldown_policy || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load MGT users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [session?.token]);

  const isSA = session?.is_super_admin === true;

  if (!isSA) {
    return (
      <div className="text-center py-20" data-testid="login-security-forbidden">
        <ShieldCheck className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
        <h2 className="text-2xl font-bold text-muted-foreground">Super Admin only</h2>
        <p className="text-muted-foreground mt-2">
          Login Security is managed by the Super Admin. Please ask your SA to grant access.
        </p>
      </div>
    );
  }

  const toggle = async (u, next) => {
    setBusy(u.mobile);
    try {
      await api.post("/auth/security/admin/toggle-password-login", {
        token: session.token,
        target_mobile: u.mobile,
        password_login_enabled: next,
      });
      toast.success(`Password login ${next ? "ENABLED" : "DISABLED"} for ${u.name}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    } finally {
      setBusy(null);
    }
  };

  const forceReset = async (u) => {
    setBusy(u.mobile);
    try {
      const r = await api.post("/auth/security/admin/force-password-reset", {
        token: session.token, target_mobile: u.mobile,
      });
      toast.success(`Password reset. ${r.data.sessions_revoked} session(s) revoked.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Force reset failed");
    } finally {
      setBusy(null);
    }
  };

  const revokeSessions = async (u) => {
    setBusy(u.mobile);
    try {
      const r = await api.post("/auth/security/admin/revoke-user-sessions", {
        token: session.token, target_mobile: u.mobile,
      });
      toast.success(`Revoked ${r.data.sessions_revoked} session(s) for ${u.name}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revoke failed");
    } finally {
      setBusy(null);
    }
  };

  const resetToDefault = async (u) => {
    setBusy(u.mobile);
    try {
      const r = await api.post("/auth/security/admin/reset-to-default", {
        token: session.token, target_mobile: u.mobile,
      });
      toast.success(
        `${u.name} reset to default password. ${r.data.sessions_revoked} session(s) revoked. `
        + `Default: ${r.data.default_password}`,
        { duration: 10000 });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset to default failed");
    } finally {
      setBusy(null);
    }
  };

  // SA-only SMTP diagnostic.
  const runEmailTest = async () => {
    const to = (testTo || "").trim();
    if (!to || !to.includes("@")) {
      toast.error("Enter a valid email address to test.");
      return;
    }
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post("/auth/security/test-email", {
        token: session.token, to_email: to,
      });
      setTestResult(r.data);
      if (r.data?.sent) toast.success(`Test email sent to ${r.data.to_masked}`);
      else toast.error(`Delivery failed: ${r.data?.failure_code || "UNKNOWN"}`);
    } catch (e) {
      setTestResult({ sent: false,
        failure_code: "HTTP_" + (e.response?.status || "ERR"),
        failure_message: e.response?.data?.detail || "Request failed" });
      toast.error(e.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const fmtLast = (iso) => {
    if (!iso) return <span className="text-muted-foreground italic">never</span>;
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  return (
    <div className="space-y-6" data-testid="login-security-page">
      {session?.is_super_admin && <UnlockAttendancePermissionCard token={session.token} />}
      <div>
        <h1 className="text-3xl font-bold text-primary flex items-center gap-2">
          <ShieldCheck className="w-8 h-8" />
          Login Security — PB-MGT Users
        </h1>
        <p className="text-muted-foreground mt-1">
          Choose the login method for each Management user individually.
          Center Managers are OTP-only by policy.
        </p>
      </div>

      {policy && (
        <Card className="bg-amber-50/60 border-amber-200">
          <CardContent className="pt-6 flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <strong>Cooldown policy:</strong> {policy.max_failed_attempts} wrong attempts →
              {" "}{policy.cooldown_seconds}-second cooldown (no permanent lockout).
              A successful login resets the counter.
              The old 30-minute lockout has been removed.
            </div>
          </CardContent>
        </Card>
      )}

      <Card data-testid="smtp-test-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" /> Test Email Delivery
          </CardTitle>
          <CardDescription>
            Send a one-off diagnostic email to confirm SMTP is reaching inboxes. The OTP
            flow uses this same transport.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              placeholder="recipient@example.com"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              data-testid="smtp-test-email-input"
              className="h-10"
            />
            <Button
              onClick={runEmailTest}
              disabled={testing}
              data-testid="smtp-test-send-btn"
              className="h-10"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        : <Mail className="w-4 h-4 mr-2" />}
              Send Test Email
            </Button>
          </div>
          {testResult && (
            <div
              className={"mt-3 rounded-md border px-3 py-2 text-sm "
                + (testResult.sent
                    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                    : "bg-rose-50 border-rose-200 text-rose-900")}
              data-testid="smtp-test-result"
            >
              {testResult.sent ? (
                <>Delivered to <strong>{testResult.to_masked}</strong></>
              ) : (
                <>
                  Delivery failed — <strong>{testResult.failure_code}</strong>:{" "}
                  {testResult.failure_message}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>MGT Users ({rows.length})</CardTitle>
          <CardDescription>
            Enable Password Login only for accounts that need it.
            Each user&apos;s password is set by them individually — you never see it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center text-muted-foreground py-10">
              No PB-MGT users found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="login-security-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Centre</TableHead>
                    <TableHead className="text-center">OTP Login</TableHead>
                    <TableHead className="text-center">Password Login</TableHead>
                    <TableHead>Login Method</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(u => (
                    <TableRow key={u.mobile}
                      data-testid={`login-security-row-${u.mobile}`}>
                      <TableCell className="font-medium">
                        {u.name || "—"}
                        {u.is_super_admin && (
                          <Badge variant="secondary" className="ml-2 text-[10px]">SA</Badge>
                        )}
                      </TableCell>
                      <TableCell>{u.mobile}</TableCell>
                      <TableCell className="text-xs">
                        {u.is_super_admin ? "Super Admin"
                          : u.is_admin ? "Admin"
                          : u.role_key || "MGT"}
                      </TableCell>
                      <TableCell>{u.center}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="text-green-700 border-green-300">ON</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Switch
                          data-testid={`toggle-password-${u.mobile}`}
                          checked={!!u.password_login_enabled}
                          disabled={busy === u.mobile || u.is_super_admin}
                          onCheckedChange={(v) => toggle(u, v)}
                          title={u.is_super_admin
                            ? "SA manages their own password from My Login Security"
                            : ""}
                        />
                      </TableCell>
                      <TableCell>
                        <Badge variant={u.password_login_enabled ? "default" : "outline"}
                          className="whitespace-nowrap">
                          {u.password_login_enabled ? "Password + OTP" : "OTP Only"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div>{fmtLast(u.last_login_at)}</div>
                        {u.last_login_method && (
                          <div className="text-muted-foreground">via {u.last_login_method}</div>
                        )}
                        {u.active_sessions > 0 && (
                          <div className="text-[10px] text-emerald-700">
                            {u.active_sessions} active session{u.active_sessions === 1 ? "" : "s"}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {u.password_login_enabled && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="outline"
                                  disabled={busy === u.mobile}
                                  data-testid={`reset-default-${u.mobile}`}
                                  title="Reset to default password">
                                  <RotateCcw className="w-3.5 h-3.5" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    Reset {u.name} to the default password?
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Existing sessions will be revoked and the user will be
                                    required to create a new personal password on the login
                                    screen before accessing the portal.
                                    You will see the one-time default password after confirming.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => resetToDefault(u)}
                                    data-testid={`confirm-reset-default-${u.mobile}`}>
                                    Reset to default
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                          {u.password_login_enabled && !u.is_super_admin && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="outline"
                                  disabled={busy === u.mobile}
                                  data-testid={`force-reset-${u.mobile}`}
                                  title="Force password reset">
                                  <KeyRound className="w-3.5 h-3.5" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Force password reset for {u.name}?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will clear their password hash, disable password login,
                                    and revoke every active session. They will be forced back to
                                    OTP-only until you re-enable password login and they set a
                                    new password.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => forceReset(u)}
                                    data-testid={`confirm-force-reset-${u.mobile}`}>
                                    Force reset
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                          {u.active_sessions > 0 && !u.is_super_admin && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="outline"
                                  disabled={busy === u.mobile}
                                  data-testid={`revoke-sessions-${u.mobile}`}
                                  title="Revoke all sessions">
                                  <LogOut className="w-3.5 h-3.5" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Revoke all sessions for {u.name}?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    They will be logged out from every device immediately.
                                    They can log back in normally afterwards — password state
                                    is not changed.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => revokeSessions(u)}>
                                    Revoke sessions
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="mt-4 flex justify-end">
            <Button variant="outline" onClick={load} size="sm"
              data-testid="refresh-login-security">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
